import { FatInvestorsPageListEntitiesQuery, FatInvestorsPageListFirmsQuery, FatInvestorsPageListInvestorsQuery } from '@/__generated__/graphql';
import { Loader } from '@/components/loaders/loader';
import { TableBodyWrapper, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import { ISort, TableColumns } from '@/components/table/types';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import useStickyFooter from '@/hooks/useStickyFooter';
import { TenantType } from '@trace-8/trace8-schema';
import { useRef } from 'react';
import styled from 'styled-components';
import { advisorColumns, entityColumns, investorColumns } from '../constants';
import AdvisorTableSumFooter from './advisorTableSumFooter';
import EntityTableSumFooter from './entityTableSumFooter';
import InvestorTableSumFooter from './investorTableSumFooter';
import MobileTableAdvisor from './mobileTable/mobileTableAdvisor';
import MobileTableEntity from './mobileTable/mobileTableEntity';
import MobileTableInvestor from './mobileTable/mobileTableInvestor';
import TableBodyAdvisor from './tableBodyAdvisor';
import TableBodyEntity from './tableBodyEntity';
import TableBodyInvestor from './tableBodyInvestor';

interface TableProps {
  sort: ISort;
  setSort: (value: ISort) => void;
  refetch: () => void;
  loading: boolean;
  viewByValue: string;
  openAdvisorFirmDetails: (id: string) => void;
  openInvestorDetailsPage: ({ investorId, entityId }: { investorId: string; entityId?: string }) => Promise<void>;
  listAdvisers: FatInvestorsPageListFirmsQuery['listFirms'];
  listInvestors: FatInvestorsPageListInvestorsQuery['listInvestors'];
  listEntity: FatInvestorsPageListEntitiesQuery['listEntities'];
}

const Table = ({
  sort,
  setSort,
  refetch,
  loading,
  viewByValue,
  openAdvisorFirmDetails,
  openInvestorDetailsPage,
  listAdvisers,
  listInvestors,
  listEntity
}: TableProps) => {
  const { isMobile } = useResponsive();
  const { user } = useAuth();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useStickyFooter(containerRef, footerRef);

  const filteredColumns = (columns: TableColumns[]) => {
    if (user && user.tenant.type === TenantType.ADVISORY_FIRM) {
      return columns.map((column) => {
        if (column.key !== 'ADVISORY_FIRMS') {
          return column;
        }
        return { ...column, width: 0, title: '' };
      });
    }

    return columns;
  };

  const TableBody = () => {
    switch (viewByValue) {
      case 'Advisory Firm':
        return isMobile ? (
          <>{loading ? <Loader /> : <MobileTableAdvisor listAdvisers={listAdvisers} changeViewBy={openAdvisorFirmDetails} />}</>
        ) : (
          <TableWrapper padding="0">
            <TableHeader refetch={refetch} columns={advisorColumns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              {loading ? (
                <Loader />
              ) : (
                <TableBodyAdvisor listAdvisers={listAdvisers.data} columns={advisorColumns} changeViewBy={openAdvisorFirmDetails} />
              )}
              <TableSumFooterWrapper ref={footerRef}>
                {!!listAdvisers.data && <AdvisorTableSumFooter columns={advisorColumns} totals={listAdvisers.totals} />}
              </TableSumFooterWrapper>
              <div ref={containerRef}></div>
            </TableBodyWrapper>
          </TableWrapper>
        );
      case 'Investor Household':
        const filteredInvestorColumns = filteredColumns(investorColumns);
        return isMobile ? (
          <>{loading ? <Loader /> : <MobileTableInvestor listInvestors={listInvestors} openInvestorDetailsPage={openInvestorDetailsPage} />}</>
        ) : (
          <TableWrapper padding="0">
            <TableHeader refetch={refetch} columns={filteredInvestorColumns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              {loading ? (
                <Loader />
              ) : (
                <TableBodyInvestor
                  listInvestors={listInvestors.data}
                  columns={filteredInvestorColumns}
                  openInvestorDetailsPage={openInvestorDetailsPage}
                />
              )}
              <TableSumFooterWrapper ref={footerRef}>
                {!!listInvestors.data && <InvestorTableSumFooter columns={filteredInvestorColumns} totals={listInvestors.totals} />}
              </TableSumFooterWrapper>
              <div ref={containerRef}></div>
            </TableBodyWrapper>
          </TableWrapper>
        );
      case 'Investor Entity':
        const filteredEntityColumns = filteredColumns(entityColumns);
        return isMobile ? (
          <>{loading ? <Loader /> : <MobileTableEntity listEntity={listEntity} openInvestorDetailsPage={openInvestorDetailsPage} />}</>
        ) : (
          <TableWrapper padding="0">
            <TableHeader refetch={refetch} columns={filteredEntityColumns} savedSort={sort} savedSetSort={setSort} />
            <TableBodyWrapper>
              {loading ? (
                <Loader />
              ) : (
                <TableBodyEntity listEntity={listEntity.data} columns={filteredEntityColumns} openInvestorDetailsPage={openInvestorDetailsPage} />
              )}
              <TableSumFooterWrapper ref={footerRef}>
                {!!listEntity.data && <EntityTableSumFooter columns={filteredEntityColumns} totals={listEntity.totals} />}
              </TableSumFooterWrapper>
              <div ref={containerRef}></div>
            </TableBodyWrapper>
          </TableWrapper>
        );
      default:
        return <></>;
    }
  };

  return <TableBody />;
};

export default Table;

const TableSumFooterWrapper = styled.div`
  &.sticky {
    position: sticky;
    bottom: 0;
    z-index: 9;
  }
`;
